<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import CanvasButton from "../CanvasButton.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import CameraOverlay from "@/components/field-observations/CameraOverlay.vue";
import PdfPagePicker from "../DrawerMenus/PdfPagePicker.vue";
import { useImageImport } from "@/composable/useImageImport";
import { trackTab } from "@/analytics/drawing-analytics";
import { toolbarPopperOptions } from "./toolbar/toolbarPopper";
import { isCanvasDrawing } from "@/stores/CanvasViewState";

/**
 * Outil Image de la barre d'outils (ex-onglet Images du volet droit) : ouvre,
 * à droite de la barre, une popup « Insérer sur le plan » — image de
 * l'appareil, photo, pages d'un PDF.
 */
const menuVisible = ref(false);

// Popup à droite de la barre (au-delà de sa 2e colonne s'il y en a une).
const imageButton = ref<InstanceType<typeof CanvasButton>>();
const popperOptions = toolbarPopperOptions(
  () => imageButton.value?.$el as Element | undefined,
  10,
);

const inputGallery = ref<HTMLInputElement | null>(null);
const inputPdf = ref<HTMLInputElement | null>(null);

const {
  showCameraOverlay,
  hasCamera,
  pdfDocument,
  onImageInputChange,
  refreshCameraAvailability,
  openCamera,
  onCameraCaptured,
  onPdfInputChange,
  closePdfPicker,
} = useImageImport();

const showPdfPicker = computed({
  get: () => pdfDocument.value !== null,
  set: (open) => {
    if (!open) closePdfPicker();
  },
});

// Dès le montage, pour que la ligne « Prendre une photo » ne surgisse pas
// popup ouverte ; puis à chaque ouverture (webcam branchée entre-temps).
onMounted(refreshCameraAvailability);

// Même suivi qu'à l'ouverture de l'ancien onglet Images.
function onShow() {
  trackTab("images", "opened");
  void refreshCameraAvailability();
}

function pickFromDevice() {
  menuVisible.value = false;
  inputGallery.value?.click();
}

function takePhoto() {
  menuVisible.value = false;
  openCamera();
}

function importPdf() {
  menuVisible.value = false;
  inputPdf.value?.click();
}
</script>

<template>
  <el-popover
    v-model:visible="menuVisible"
    placement="right-start"
    :width="320"
    trigger="click"
    :show-arrow="false"
    :popper-options="popperOptions"
    :popper-class="`canvas-popper canvas-image-popper${isCanvasDrawing ? ' drawing-active' : ''}`"
    @show="onShow"
  >
    <template #reference>
      <CanvasButton
        ref="imageButton"
        phosphor-icon="image"
        :active="menuVisible"
        :tooltip-title="$t('canvas:tools.image.tooltip.title')"
        :tooltip-text="$t('canvas:tools.image.tooltip.text')"
      />
    </template>

    <div class="image-menu" role="menu">
      <div class="image-menu-section">
        {{ $t("canvas:tools.image.section") }}
      </div>
      <button
        type="button"
        class="image-menu-item"
        role="menuitem"
        @click="pickFromDevice"
      >
        <span class="image-menu-icon">
          <PhosphorIcon name="images" :size="19" />
        </span>
        <span class="d-flex flex-column">
          <span class="image-menu-title">
            {{ $t("canvas:tools.image.gallery.title") }}
          </span>
          <span class="image-menu-hint">
            {{ $t("canvas:tools.image.gallery.hint") }}
          </span>
        </span>
      </button>
      <button
        v-if="hasCamera"
        type="button"
        class="image-menu-item"
        role="menuitem"
        @click="takePhoto"
      >
        <span class="image-menu-icon">
          <PhosphorIcon name="camera" :size="19" />
        </span>
        <span class="d-flex flex-column">
          <span class="image-menu-title">
            {{ $t("canvas:tools.image.camera.title") }}
          </span>
          <span class="image-menu-hint">
            {{ $t("canvas:tools.image.camera.hint") }}
          </span>
        </span>
      </button>
      <button
        type="button"
        class="image-menu-item"
        role="menuitem"
        @click="importPdf"
      >
        <span class="image-menu-icon">
          <PhosphorIcon name="file-pdf" :size="19" />
        </span>
        <span class="d-flex flex-column">
          <span class="image-menu-title">
            {{ $t("canvas:tools.image.pdf.title") }}
          </span>
          <span class="image-menu-hint">
            {{ $t("canvas:tools.image.pdf.hint") }}
          </span>
        </span>
      </button>
    </div>
  </el-popover>

  <!-- Hors de la popup : elle se ferme avant que le fichier soit choisi -->
  <input
    ref="inputGallery"
    type="file"
    accept="image/*"
    class="d-none"
    @change="onImageInputChange"
  />
  <input
    ref="inputPdf"
    type="file"
    accept="application/pdf"
    class="d-none"
    @change="onPdfInputChange"
  />

  <!-- Caméra intégrée (tablette et webcam), téléportée : la barre d'outils
       est zoomée, un `position: fixed` y serait mis à l'échelle. -->
  <Teleport to="body">
    <CameraOverlay
      v-if="showCameraOverlay"
      @close="showCameraOverlay = false"
      @captured="onCameraCaptured"
    />
  </Teleport>

  <!-- Choix des pages du PDF : dans une modale, il occupait le volet -->
  <DRDialog
    v-model="showPdfPicker"
    :title="$t('canvas:tools.image.pdf.title')"
    :close-on-click-modal="false"
    hide-footer
    :width="560"
  >
    <div class="pdf-picker-frame">
      <PdfPagePicker
        v-if="pdfDocument"
        :pdf-document="pdfDocument"
        @close="closePdfPicker"
      />
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped>
.image-menu {
  display: flex;
  flex-direction: column;
}

.image-menu-section {
  padding: 4px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
}

.image-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--canvas-btn-hover-bg);
  }
}

.image-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  background-color: rgba(255, 255, 255, 0.08);
}

.image-menu-title {
  font-size: 13.5px;
  font-weight: 600;
}

.image-menu-hint {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

// Hauteur fixe : la grille des pages défile à l'intérieur.
.pdf-picker-frame {
  height: min(560px, 70vh);
}
</style>
